import { Loading } from "../../app/Status";
import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import {
  addMonthsIso,
  DOC_CATEGORIES,
  DOC_CATEGORY_LABELS,
  DOC_MAX_BYTES,
  DOC_MIMES,
  DOC_MIMES_LABEL,
  DOC_VISIBILITIES,
  DOC_VISIBILITY_HINTS,
  DOC_VISIBILITY_LABELS,
  docMimeOf,
  DRAWING_DISCIPLINES,
  parseDrawingName,
  suggestDocType,
  type BuildingDto,
  type DocumentDetailDto,
  type DocumentDto,
  type DocumentTypeDto,
  type DocVisibility,
  type DrawingDiscipline,
} from "@klantpunt/shared";
import { Modal } from "../../app/Modal";
import { IconCamera, IconTrash, IconUpload } from "../../app/icons";
import { api, ApiError } from "../../lib/api";
import { useMe } from "../../lib/auth";
import { useToast } from "../../lib/toast";
import { fmtDate, fmtSize, sha256Of, uploadDocumentFile, useDossierRefresh } from "./useDossier";
import { DirectorySuggestions } from "../directory/Pick";

export const errMsg = (e: unknown) => (e instanceof ApiError || e instanceof Error ? e.message : "Er ging iets mis");
const seesAll = (role: string | undefined) => role === "owner" || role === "beheerder";

/** Keuzelijst met documentsoorten, gegroepeerd per rubriek. */
export function TypeSelect({
  types,
  value,
  onChange,
  only,
  id,
}: {
  types: DocumentTypeDto[];
  value: string;
  onChange: (id: string) => void;
  /** Alleen tekeningsoorten (true) of juist geen (false). */
  only?: "drawing" | "document";
  id?: string;
}) {
  const usable = types.filter(
    (t) => (t.active || t.id === value) && (only === "drawing" ? t.drawing : only === "document" ? !t.drawing : true),
  );
  return (
    <select id={id} value={value} onChange={(e) => onChange(e.target.value)}>
      {!value && <option value="">Kies een soort…</option>}
      {DOC_CATEGORIES.map((c) => {
        const list = usable.filter((t) => t.category === c);
        if (!list.length) return null;
        return (
          <optgroup key={c} label={DOC_CATEGORY_LABELS[c]}>
            {list.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </optgroup>
        );
      })}
    </select>
  );
}

function VisibilityField({
  value,
  onChange,
  locked,
  canConfidential,
}: {
  value: DocVisibility;
  onChange: (v: DocVisibility) => void;
  locked?: boolean;
  /** Alleen een beheerder zet vertrouwelijk aan of uit. */
  canConfidential: boolean;
}) {
  const disabled = !canConfidential && value === "vertrouwelijk";
  return (
    <label className="field">
      <span className="label">Zichtbaar voor</span>
      <select value={value} onChange={(e) => onChange(e.target.value as DocVisibility)} disabled={locked || disabled}>
        {DOC_VISIBILITIES.filter((v) => canConfidential || v !== "vertrouwelijk" || value === v).map((v) => (
          <option key={v} value={v}>
            {DOC_VISIBILITY_LABELS[v]}
          </option>
        ))}
      </select>
      <span className="hint">
        {locked ? "Deze soort is altijd vertrouwelijk." : DOC_VISIBILITY_HINTS[value]}
        {disabled && !locked && " Alleen een beheerder kan vertrouwelijk aan- of uitzetten."}
      </span>
    </label>
  );
}

const titleOf = (name: string) =>
  name
    .replace(/\.[A-Za-z0-9]{1,5}$/, "")
    .replace(/[_]+/g, " ")
    .trim() || name;

/** Titel van een tekening zonder nummer en revisie ("W-101 LBK schema rev C.pdf" → "LBK schema"). */
function drawingTitleOf(name: string, number: string, revision: string | null) {
  const base = titleOf(name)
    .replace(new RegExp(number.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "i"), " ")
    .replace(revision ? new RegExp(`(^|[\\s-])rev(isie)?[\\s.-]?${revision}(?=$|[\\s.-])`, "i") : /$^/, " ")
    .replace(/^[\s-]+|[\s-]+$/g, "")
    .replace(/\s{2,}/g, " ");
  return base || titleOf(name);
}

interface Item {
  key: string;
  file: File;
  title: string;
  typeId: string;
  drawingNumber: string;
  revision: string;
  discipline: DrawingDiscipline | "";
  issuedOn: string;
  validUntil: string;
  /** Leeg = automatisch uit de geldigheid van de soort. */
  validTouched: boolean;
  visibility: DocVisibility | null;
  sha: string | null;
  dups: { id: string; title: string; version: number }[];
  /** Als nieuwe versie van dit bestaande document. */
  asVersionOf: string | null;
  error: string | null;
}

/**
 * Uploaden naar het dossier: één of meer bestanden tegelijk. Per bestand een voorstel voor soort, titel en
 * (bij tekeningen) nummer en revisie uit de bestandsnaam. Een bestand dat al in het dossier staat, wordt gemeld;
 * dan kan het als nieuwe versie van dat document.
 */
export function UploadDialog({
  building,
  types,
  files: initial,
  preset,
  onClose,
  onDone,
  onScan,
}: {
  building: BuildingDto;
  types: DocumentTypeDto[];
  files?: File[];
  preset?: { drawing?: boolean; assetIds?: string[]; typeKey?: string; title?: string };
  onClose: () => void;
  onDone?: (docIds: string[]) => void;
  /** Scannen met de camera in plaats van een bestand kiezen. */
  onScan?: () => void;
}) {
  const toast = useToast();
  const { data: me } = useMe();
  const refresh = useDossierRefresh(building.id);
  const [items, setItems] = useState<Item[]>([]);
  const [floorId, setFloorId] = useState("");
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const byId = useMemo(() => new Map(types.map((t) => [t.id, t])), [types]);

  const update = (key: string, patch: Partial<Item>) =>
    setItems((list) => list.map((it) => (it.key === key ? { ...it, ...patch } : it)));

  const add = (fl: FileList | File[]) => {
    const next: Item[] = [];
    for (const file of [...fl]) {
      const mime = docMimeOf(file.name, file.type);
      const bad = !(DOC_MIMES as readonly string[]).includes(mime)
        ? `Alleen ${DOC_MIMES_LABEL}`
        : file.size > DOC_MAX_BYTES
          ? `Te groot (max ${Math.round(DOC_MAX_BYTES / 1048576)} MB)`
          : null;
      const presetType = preset?.typeKey ? types.find((t) => t.key === preset.typeKey) : null;
      const drawingType = types.find((t) => t.key === "drawing");
      const t =
        presetType ??
        (preset?.drawing ? drawingType : null) ??
        suggestDocType(file.name, types) ??
        types.find((x) => x.key === "document") ??
        null;
      const parsed = t?.drawing ? parseDrawingName(file.name) : null;
      next.push({
        key: crypto.randomUUID(),
        file,
        title:
          preset?.title && fl.length === 1
            ? preset.title
            : parsed
              ? drawingTitleOf(file.name, parsed.number, parsed.revision)
              : titleOf(file.name),
        typeId: t?.id ?? "",
        drawingNumber: parsed?.number ?? "",
        revision: parsed?.revision ?? "",
        discipline: "",
        issuedOn: "",
        validUntil: "",
        validTouched: false,
        visibility: null,
        sha: null,
        dups: [],
        asVersionOf: null,
        error: bad,
      });
    }
    setItems((list) => [...list, ...next]);
    // Vingerafdruk en dubbele bestanden op de achtergrond.
    for (const it of next) {
      if (it.error) continue;
      void sha256Of(it.file).then(async (sha) => {
        if (!sha) return;
        const dups = await api<Item["dups"]>(`/buildings/${building.id}/dossier/duplicates?sha256=${sha}`).catch(
          () => [],
        );
        update(it.key, { sha, dups });
      });
    }
  };

  useEffect(() => {
    if (initial?.length) add(initial);
    // Alleen bij openen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const autoValid = (it: Item) => {
    const t = byId.get(it.typeId);
    return it.issuedOn && t?.validityMonths ? addMonthsIso(it.issuedOn, t.validityMonths) : "";
  };

  const ready = items.filter((it) => !it.error);
  const save = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!ready.length) return;
    if (ready.some((it) => !it.title.trim() || !it.typeId)) {
      setError("Geef elk bestand een titel en een soort.");
      return;
    }
    setBusy(true);
    setError(null);
    const done: string[] = [];
    try {
      let n = 0;
      for (const it of ready) {
        n++;
        setProgress(`Bestand ${n} van ${ready.length} uploaden…`);
        const t = byId.get(it.typeId)!;
        const fileId = await uploadDocumentFile(it.file, it.file.name);
        const version = {
          fileId,
          issuedOn: it.issuedOn || null,
          ...(it.validTouched ? { validUntil: it.validUntil || null } : {}),
          revision: t.drawing ? it.revision.trim() || null : null,
          sha256: it.sha,
        };
        if (it.asVersionOf) {
          await api(`/documents/${it.asVersionOf}/versions`, { method: "POST", body: version });
          done.push(it.asVersionOf);
        } else {
          const id = crypto.randomUUID();
          await api<DocumentDetailDto>(`/buildings/${building.id}/documents`, {
            method: "POST",
            body: {
              id,
              typeId: t.id,
              title: it.title.trim(),
              drawingNumber: t.drawing ? it.drawingNumber.trim() || null : null,
              discipline: t.drawing ? it.discipline || null : null,
              floorId: floorId || null,
              ...(it.visibility ? { visibility: it.visibility } : {}),
              version,
              links: { assetIds: preset?.assetIds ?? [], roomIds: [] },
            },
          });
          done.push(id);
        }
        setItems((list) => list.filter((x) => x.key !== it.key));
      }
      await refresh();
      toast(done.length === 1 ? "Document opgeslagen" : `${done.length} documenten opgeslagen`);
      onDone?.(done);
      onClose();
    } catch (err) {
      await refresh();
      setError(`${errMsg(err)}${done.length ? ` (${done.length} al opgeslagen)` : ""}`);
      setBusy(false);
      setProgress(null);
    }
  };

  return (
    <Modal
      title={preset?.drawing ? "Tekeningen uploaden" : "Documenten uploaden"}
      onClose={onClose}
      busy={busy}
      wide
      footer={
        <>
          {progress && <span className="muted small">{progress}</span>}
          <span className="grow" />
          <button type="button" className="btn ghost" onClick={onClose} disabled={busy}>
            Annuleren
          </button>
          <button type="button" className="btn primary" onClick={() => void save()} disabled={busy || !ready.length}>
            {ready.length > 1 ? `${ready.length} opslaan` : "Opslaan"}
          </button>
        </>
      }
    >
      <form onSubmit={(e) => void save(e)} className="doc-upload">
        {error && (
          <div className="alert" role="alert">
            {error}
          </div>
        )}
        <div
          className="doc-drop"
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            if (e.dataTransfer.files.length) add(e.dataTransfer.files);
          }}
        >
          <IconUpload aria-hidden="true" />
          <span>Sleep bestanden hierheen of</span>
          <button type="button" className="btn small" onClick={() => input.current?.click()} disabled={busy}>
            Kies bestanden
          </button>
          {onScan && items.length === 0 && (
            <button type="button" className="btn small" onClick={onScan} disabled={busy}>
              <IconCamera size={18} aria-hidden="true" /> Scannen
            </button>
          )}
          <input
            ref={input}
            type="file"
            multiple
            hidden
            accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx,.txt,.dwg,.dxf,.ifc,.zip"
            onChange={(e) => {
              if (e.target.files) add(e.target.files);
              e.target.value = "";
            }}
          />
          <span className="muted small">{DOC_MIMES_LABEL}, max 100 MB per bestand.</span>
        </div>

        {items.length > 0 && (
          <ul className="doc-items">
            {items.map((it) => {
              const t = byId.get(it.typeId);
              const auto = autoValid(it);
              const fid = `doc-${it.key}`;
              return (
                <li key={it.key} className={it.error ? "doc-item bad" : "doc-item"}>
                  <div className="doc-itemhead">
                    <strong className="grow doc-fname">{it.file.name}</strong>
                    <span className="muted small num">{fmtSize(it.file.size)}</span>
                    <button
                      type="button"
                      className="btn ghost ico small"
                      aria-label={`${it.file.name} weghalen`}
                      onClick={() => setItems((l) => l.filter((x) => x.key !== it.key))}
                      disabled={busy}
                    >
                      <IconTrash size={18} aria-hidden="true" />
                    </button>
                  </div>
                  {it.error ? (
                    <p className="small doc-err">{it.error}</p>
                  ) : (
                    <>
                      {it.dups.length > 0 && (
                        <div className="doc-dup small">
                          <p>
                            Dit bestand staat al in het dossier:{" "}
                            {it.dups.map((d) => `${d.title} (versie ${d.version})`).join(", ")}.
                          </p>
                          <label>
                            <input
                              type="checkbox"
                              checked={!!it.asVersionOf}
                              onChange={(e) =>
                                update(it.key, { asVersionOf: e.target.checked ? it.dups[0]!.id : null })
                              }
                            />{" "}
                            Toch opslaan als nieuwe versie van {it.dups[0]!.title}
                          </label>
                        </div>
                      )}
                      {!it.asVersionOf && (
                        <div className="grid2">
                          <label className="field">
                            <span className="label">Titel *</span>
                            <input value={it.title} onChange={(e) => update(it.key, { title: e.target.value })} />
                          </label>
                          <label className="field" htmlFor={`${fid}-type`}>
                            <span className="label">Soort *</span>
                            <TypeSelect
                              id={`${fid}-type`}
                              types={types}
                              value={it.typeId}
                              onChange={(v) => {
                                const nt = byId.get(v);
                                const parsed = nt?.drawing && !it.drawingNumber ? parseDrawingName(it.file.name) : null;
                                update(it.key, {
                                  typeId: v,
                                  visibility: null,
                                  ...(parsed ? { drawingNumber: parsed.number, revision: parsed.revision ?? "" } : {}),
                                });
                              }}
                            />
                          </label>
                        </div>
                      )}
                      {t?.drawing && (
                        <div className="grid3">
                          {!it.asVersionOf && (
                            <label className="field">
                              <span className="label">Tekeningnummer</span>
                              <input
                                value={it.drawingNumber}
                                onChange={(e) => update(it.key, { drawingNumber: e.target.value })}
                              />
                            </label>
                          )}
                          <label className="field">
                            <span className="label">Revisie</span>
                            <input value={it.revision} onChange={(e) => update(it.key, { revision: e.target.value })} />
                          </label>
                          {!it.asVersionOf && (
                            <label className="field">
                              <span className="label">Discipline</span>
                              <select
                                value={it.discipline}
                                onChange={(e) => update(it.key, { discipline: e.target.value as DrawingDiscipline })}
                              >
                                <option value="">–</option>
                                {DRAWING_DISCIPLINES.map((d) => (
                                  <option key={d} value={d}>
                                    {d}
                                  </option>
                                ))}
                              </select>
                            </label>
                          )}
                        </div>
                      )}
                      {!t?.drawing && (
                        <div className="grid2">
                          <label className="field">
                            <span className="label">Afgegeven op</span>
                            <input
                              type="date"
                              value={it.issuedOn}
                              onChange={(e) => update(it.key, { issuedOn: e.target.value })}
                            />
                          </label>
                          <label className="field">
                            <span className="label">Geldig tot</span>
                            <input
                              type="date"
                              value={it.validTouched ? it.validUntil : auto}
                              onChange={(e) => update(it.key, { validUntil: e.target.value, validTouched: true })}
                            />
                            {!it.validTouched && auto && t?.validityMonths && (
                              <span className="hint">
                                Uit de soort:{" "}
                                {t.validityMonths % 12 === 0
                                  ? `${t.validityMonths / 12} jaar`
                                  : `${t.validityMonths} maanden`}{" "}
                                geldig.
                              </span>
                            )}
                          </label>
                        </div>
                      )}
                      {!it.asVersionOf && t && (
                        <VisibilityField
                          value={t.confidential ? "vertrouwelijk" : (it.visibility ?? t.visibility)}
                          locked={t.confidential}
                          canConfidential={seesAll(me?.role ?? undefined)}
                          onChange={(v) => update(it.key, { visibility: v })}
                        />
                      )}
                    </>
                  )}
                </li>
              );
            })}
          </ul>
        )}
        {items.length > 0 && building.floors.length > 0 && (
          <label className="field">
            <span className="label">Verdieping (optioneel)</span>
            <select value={floorId} onChange={(e) => setFloorId(e.target.value)}>
              <option value="">Hele gebouw</option>
              {building.floors.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
          </label>
        )}
        {(preset?.assetIds?.length ?? 0) > 0 && (
          <p className="hint">Wordt gekoppeld aan deze asset en verschijnt ook in het dossier van het gebouw.</p>
        )}
      </form>
    </Modal>
  );
}

/** Nieuwe versie van een bestaand document (bijvoorbeeld een nieuw certificaat of een revisie van een tekening). */
export function VersionDialog({ doc, onClose }: { doc: DocumentDto; onClose: () => void }) {
  const toast = useToast();
  const refresh = useDossierRefresh(doc.buildingId);
  const [file, setFile] = useState<File | null>(null);
  const [issuedOn, setIssuedOn] = useState("");
  const [validUntil, setValidUntil] = useState<string | null>(null);
  const [revision, setRevision] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const save = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!file) return;
    const mime = docMimeOf(file.name, file.type);
    if (!(DOC_MIMES as readonly string[]).includes(mime)) return setError(`Alleen ${DOC_MIMES_LABEL}`);
    setBusy(true);
    setError(null);
    try {
      const sha = await sha256Of(file);
      const fileId = await uploadDocumentFile(file, file.name);
      await api(`/documents/${doc.id}/versions`, {
        method: "POST",
        body: {
          fileId,
          issuedOn: issuedOn || null,
          ...(validUntil !== null ? { validUntil: validUntil || null } : {}),
          revision: doc.type.drawing ? revision.trim() || null : null,
          note: note.trim() || null,
          sha256: sha,
        },
      });
      await refresh(doc.id);
      toast("Nieuwe versie opgeslagen");
      onClose();
    } catch (err) {
      setError(errMsg(err));
      setBusy(false);
    }
  };
  return (
    <Modal
      title="Nieuwe versie"
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <span className="grow" />
          <button type="button" className="btn ghost" onClick={onClose} disabled={busy}>
            Annuleren
          </button>
          <button type="button" className="btn primary" onClick={() => void save()} disabled={busy || !file}>
            {busy ? "Uploaden…" : "Opslaan"}
          </button>
        </>
      }
    >
      <form onSubmit={(e) => void save(e)}>
        {error && (
          <div className="alert" role="alert">
            {error}
          </div>
        )}
        <p className="muted small">
          {doc.title} · nu versie {doc.current.version}
          {doc.revision && `, revisie ${doc.revision}`}. De oude versie blijft bewaard.
        </p>
        {doc.current.annotationCount > 0 && (
          <p className="note small">
            Op versie {doc.current.version}{" "}
            {doc.current.annotationCount === 1 ? "is 1 wijziging" : `zijn ${doc.current.annotationCount} wijzigingen`}{" "}
            ingetekend. Controleer of die in de nieuwe versie verwerkt zijn; de markeringen blijven bij versie{" "}
            {doc.current.version} bewaard.
          </p>
        )}
        <label className="field">
          <span className="label">Bestand *</span>
          <input
            type="file"
            onChange={(e) => {
              const f = e.target.files?.[0] ?? null;
              setFile(f);
              const p = f && doc.type.drawing ? parseDrawingName(f.name) : null;
              if (p?.revision) setRevision(p.revision);
            }}
          />
        </label>
        {doc.type.drawing ? (
          <label className="field">
            <span className="label">Revisie</span>
            <input value={revision} onChange={(e) => setRevision(e.target.value)} placeholder="bijv. D" />
          </label>
        ) : (
          <div className="grid2">
            <label className="field">
              <span className="label">Afgegeven op</span>
              <input type="date" value={issuedOn} onChange={(e) => setIssuedOn(e.target.value)} />
            </label>
            <label className="field">
              <span className="label">Geldig tot</span>
              <input type="date" value={validUntil ?? ""} onChange={(e) => setValidUntil(e.target.value)} />
              {validUntil === null && <span className="hint">Leeg: uit de geldigheid van de soort.</span>}
            </label>
          </div>
        )}
        <label className="field">
          <span className="label">Wat is er veranderd?</span>
          <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} />
        </label>
      </form>
    </Modal>
  );
}

/** Gegevens van een document bewerken (titel, soort, nummer, verdieping, verantwoordelijke, zichtbaarheid). */
export function EditDialog({
  doc,
  building,
  types,
  members,
  onClose,
}: {
  doc: DocumentDto;
  building: BuildingDto;
  types: DocumentTypeDto[];
  members: { id: string; name: string }[];
  onClose: () => void;
}) {
  const toast = useToast();
  const { data: me } = useMe();
  const refresh = useDossierRefresh(doc.buildingId);
  const [f, setF] = useState({
    title: doc.title,
    typeId: doc.type.id,
    reference: doc.reference ?? "",
    drawingNumber: doc.drawingNumber ?? "",
    discipline: doc.discipline ?? "",
    floorId: doc.floor?.id ?? "",
    responsibleId: doc.responsible?.id ?? "",
    issuer: doc.issuer ?? "",
    visibility: doc.visibility,
    offline: doc.offline,
    notes: doc.notes ?? "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (p: Partial<typeof f>) => setF((x) => ({ ...x, ...p }));
  const t = types.find((x) => x.id === f.typeId);
  const save = async (e?: FormEvent) => {
    e?.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api(`/documents/${doc.id}`, {
        method: "PATCH",
        body: {
          title: f.title.trim(),
          typeId: f.typeId,
          reference: f.reference.trim() || null,
          drawingNumber: f.drawingNumber.trim() || null,
          discipline: f.discipline || null,
          floorId: f.floorId || null,
          responsibleId: f.responsibleId || null,
          issuer: f.issuer.trim() || null,
          ...(f.visibility !== doc.visibility ? { visibility: f.visibility } : {}),
          offline: f.offline,
          notes: f.notes.trim() || null,
        },
      });
      await refresh(doc.id);
      toast("Document opgeslagen");
      onClose();
    } catch (err) {
      setError(errMsg(err));
      setBusy(false);
    }
  };
  return (
    <Modal
      title="Document bewerken"
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <span className="grow" />
          <button type="button" className="btn ghost" onClick={onClose} disabled={busy}>
            Annuleren
          </button>
          <button type="button" className="btn primary" onClick={() => void save()} disabled={busy || !f.title.trim()}>
            Opslaan
          </button>
        </>
      }
    >
      <form onSubmit={(e) => void save(e)}>
        {error && (
          <div className="alert" role="alert">
            {error}
          </div>
        )}
        <label className="field">
          <span className="label">Titel *</span>
          <input value={f.title} onChange={(e) => set({ title: e.target.value })} />
        </label>
        <div className="grid2">
          <label className="field" htmlFor="doc-edit-type">
            <span className="label">Soort</span>
            <TypeSelect
              id="doc-edit-type"
              types={types}
              value={f.typeId}
              only={doc.type.drawing ? "drawing" : "document"}
              onChange={(v) => set({ typeId: v })}
            />
          </label>
          <label className="field">
            <span className="label">Kenmerk of nummer</span>
            <input value={f.reference} onChange={(e) => set({ reference: e.target.value })} />
          </label>
        </div>
        {t?.drawing && (
          <div className="grid2">
            <label className="field">
              <span className="label">Tekeningnummer</span>
              <input value={f.drawingNumber} onChange={(e) => set({ drawingNumber: e.target.value })} />
            </label>
            <label className="field">
              <span className="label">Discipline</span>
              <select value={f.discipline} onChange={(e) => set({ discipline: e.target.value })}>
                <option value="">–</option>
                {DRAWING_DISCIPLINES.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </label>
          </div>
        )}
        <div className="grid2">
          <label className="field">
            <span className="label">Verdieping</span>
            <select value={f.floorId} onChange={(e) => set({ floorId: e.target.value })}>
              <option value="">Hele gebouw</option>
              {building.floors.map((fl) => (
                <option key={fl.id} value={fl.id}>
                  {fl.name}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="label">Afgegeven door</span>
            <input
              value={f.issuer}
              list="doc-issuer-suggestions"
              onChange={(e) => set({ issuer: e.target.value })}
              placeholder="bijv. gemeente, adviseur"
            />
            <DirectorySuggestions id="doc-issuer-suggestions" query={{ kind: undefined, buildingId: doc.buildingId }} />
          </label>
        </div>
        <label className="field">
          <span className="label">Verantwoordelijke</span>
          <select value={f.responsibleId} onChange={(e) => set({ responsibleId: e.target.value })}>
            <option value="">Niemand</option>
            {members.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
          <span className="hint">Krijgt de herinnering als het document bijna verloopt.</span>
        </label>
        <VisibilityField
          value={f.visibility}
          locked={!!t?.confidential}
          canConfidential={seesAll(me?.role ?? undefined)}
          onChange={(v) => set({ visibility: v })}
        />
        <label className="check">
          <input type="checkbox" checked={f.offline} onChange={(e) => set({ offline: e.target.checked })} /> Ook offline
          beschikbaar op de telefoon
        </label>
        <label className="field">
          <span className="label">Notities</span>
          <textarea rows={3} value={f.notes} onChange={(e) => set({ notes: e.target.value })} />
        </label>
      </form>
    </Modal>
  );
}

/** Koppelen aan assets en ruimtes van hetzelfde gebouw. */
export function LinksDialog({
  doc,
  building,
  onClose,
}: {
  doc: DocumentDto;
  building: BuildingDto;
  onClose: () => void;
}) {
  const toast = useToast();
  const refresh = useDossierRefresh(doc.buildingId);
  const [assets, setAssets] = useState(new Set(doc.links.filter((l) => l.kind === "asset").map((l) => l.id)));
  const [roomIds, setRoomIds] = useState(new Set(doc.links.filter((l) => l.kind === "room").map((l) => l.id)));
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const needle = q.trim().toLowerCase();
  const toggle = (set: Set<string>, id: string, fn: (s: Set<string>) => void) => {
    const n = new Set(set);
    if (n.has(id)) n.delete(id);
    else n.add(id);
    fn(n);
  };
  const assetRows = building.assets
    .filter((a) => !needle || `${a.code} ${a.name}`.toLowerCase().includes(needle) || assets.has(a.id))
    .slice(0, 200);
  const floorName = new Map(building.floors.map((f) => [f.id, f.name]));
  const roomRows = building.rooms
    .filter(
      (r) =>
        !needle || `${r.name} ${floorName.get(r.floorId) ?? ""}`.toLowerCase().includes(needle) || roomIds.has(r.id),
    )
    .slice(0, 200);
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await api(`/documents/${doc.id}/links`, {
        method: "PUT",
        body: { assetIds: [...assets], roomIds: [...roomIds] },
      });
      await refresh(doc.id);
      toast("Koppelingen opgeslagen");
      onClose();
    } catch (err) {
      setError(errMsg(err));
      setBusy(false);
    }
  };
  return (
    <Modal
      title="Koppelen"
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <span className="muted small">
            {assets.size} {assets.size === 1 ? "asset" : "assets"}, {roomIds.size}{" "}
            {roomIds.size === 1 ? "ruimte" : "ruimtes"}
          </span>
          <span className="grow" />
          <button type="button" className="btn ghost" onClick={onClose} disabled={busy}>
            Annuleren
          </button>
          <button type="button" className="btn primary" onClick={() => void save()} disabled={busy}>
            Opslaan
          </button>
        </>
      }
    >
      {error && (
        <div className="alert" role="alert">
          {error}
        </div>
      )}
      <p className="muted small">
        Een document aan een hoofdasset geldt ook voor de onderdelen. Koppel een tekening dus aan de installatie als
        geheel.
      </p>
      <input
        type="search"
        aria-label="Zoeken op code, naam of ruimte"
        placeholder="Zoeken op code, naam of ruimte"
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      <fieldset className="doc-pick">
        <legend>Assets</legend>
        {assetRows.length === 0 && <p className="muted small">Geen assets gevonden.</p>}
        {assetRows.map((a) => (
          <label key={a.id} className={a.parentAssetId ? "doc-pickrow part" : "doc-pickrow"}>
            <input type="checkbox" checked={assets.has(a.id)} onChange={() => toggle(assets, a.id, setAssets)} />
            <span className="code">{a.code}</span> {a.name}
          </label>
        ))}
      </fieldset>
      <fieldset className="doc-pick">
        <legend>Ruimtes</legend>
        {roomRows.length === 0 && <p className="muted small">Geen ruimtes gevonden.</p>}
        {roomRows.map((r) => (
          <label key={r.id} className="doc-pickrow">
            <input type="checkbox" checked={roomIds.has(r.id)} onChange={() => toggle(roomIds, r.id, setRoomIds)} />
            {r.name} <span className="muted small">{floorName.get(r.floorId)}</span>
          </label>
        ))}
      </fieldset>
    </Modal>
  );
}

/** Prullenbak: verwijderde documenten blijven 30 dagen terug te zetten. */
export function TrashDialog({ buildingId, onClose }: { buildingId: string; onClose: () => void }) {
  const toast = useToast();
  const refresh = useDossierRefresh(buildingId);
  const [list, setList] = useState<DocumentDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    api<DocumentDto[]>(`/buildings/${buildingId}/dossier/trash`).then(setList, (e) => setError(errMsg(e)));
  }, [buildingId]);
  const restore = async (d: DocumentDto) => {
    try {
      await api(`/documents/${d.id}/restore`, { method: "POST" });
      setList((l) => l?.filter((x) => x.id !== d.id) ?? null);
      await refresh(d.id);
      toast(`${d.title} teruggezet`);
    } catch (err) {
      setError(errMsg(err));
    }
  };
  const daysLeft = (iso: string) => Math.max(0, 30 - Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000));
  return (
    <Modal title="Prullenbak" onClose={onClose}>
      {error && (
        <div className="alert" role="alert">
          {error}
        </div>
      )}
      <p className="muted small">
        Verwijderde documenten blijven 30 dagen bewaard. Daarna worden ze definitief gewist.
      </p>
      {list === null ? (
        <Loading />
      ) : list.length === 0 ? (
        <p className="muted">De prullenbak is leeg.</p>
      ) : (
        <ul className="dir-items">
          {list.map((d) => (
            <li key={d.id}>
              <span className="grow">
                <strong>{d.title}</strong>
                <span className="muted small block">
                  {d.type.name} · verwijderd op {fmtDate(d.deletedAt)} · nog {daysLeft(d.deletedAt!)} dagen
                </span>
              </span>
              <button type="button" className="btn small" onClick={() => void restore(d)}>
                Terugzetten
              </button>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}
